import { useLanguage } from 'features/hooks/useLanguage';
import { LanguageType } from 'types';
import { parseXls } from './parse-xls/parse-xls';
import { toast } from 'react-toastify';
import { ImportMode } from '../../ImportCodelistPage';
import { useState } from 'react';
import Papa from 'papaparse';

export const SelectFile = ({
	initialImportMode,
	onFileSelected
}: {
	initialImportMode: ImportMode;
	onFileSelected: (args: {
		columns: string[];
		rows: Record<string, string>[];
		importMode: ImportMode;
	}) => void;
}) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const [importMode, setImportMode] = useState<ImportMode>(initialImportMode);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = e.target.files?.[0];
		if (!selectedFile) {
			return;
		}

		const allowedTypes = [
			'text/csv',
			'application/vnd.ms-excel',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		];
		const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();

		if (
			!allowedTypes.includes(selectedFile.type) &&
			fileExtension !== 'csv' &&
			fileExtension !== 'xls' &&
			fileExtension !== 'xlsx'
		) {
			alert('Only CSV or Excel files are allowed.');
			return;
		}

		if (fileExtension === 'csv') {
			const reader = new FileReader();
			reader.onload = event => {
				const text = event.target?.result as string;
				const parsed = parseCsv(text);
				onFileSelected({ ...parsed, importMode });
			};
			reader.readAsText(selectedFile);
		} else if (fileExtension === 'xls' || fileExtension === 'xlsx') {
			parseXls(selectedFile)
				.then(parsed => {
					onFileSelected({ ...parsed, importMode });
				})
				.catch(error => {
					const isPasswordProtected = error?.message === 'File is password-protected';

					if (isPasswordProtected) {
						toast.error(translation.passwordProtectedError, {
							position: 'top-center'
						});
					} else {
						toast.error(translation.parseError, {
							position: 'top-center'
						});
					}

					e.target.value = '';
				});
		}
	};

	return (
		<div>
			<h2 className="text-2xl font-bold">{translation.title}</h2>

			<p className="text-base mt-4">{translation.description}</p>

			<div className="mt-6">
				<h3 className="text-base font-bold">{translation.importModeLabel}</h3>

				<div className="mt-3 flex flex-col gap-3">
					<label className="flex items-start gap-3 cursor-pointer">
						<input
							type="radio"
							name="importMode"
							value="full"
							checked={importMode === 'full'}
							onChange={() => setImportMode('full')}
							className="mt-1"
						/>
						<div>
							<span className="text-base font-medium">
								{translation.fullReplacementLabel}
							</span>
							<p className="text-sm text-gray-600">
								{translation.fullReplacementDescription}
							</p>
						</div>
					</label>

					<label className="flex items-start gap-3 cursor-pointer">
						<input
							type="radio"
							name="importMode"
							value="partial"
							checked={importMode === 'partial'}
							onChange={() => setImportMode('partial')}
							className="mt-1"
						/>
						<div>
							<span className="text-base font-medium">
								{translation.partialUpdateLabel}
							</span>
							<p className="text-sm text-gray-600">
								{translation.partialUpdateDescription}
							</p>
						</div>
					</label>
				</div>
			</div>

			<div className="mt-10">
				<input type="file" onChange={handleFileChange} />
			</div>
		</div>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		title: 'Select file',
		description: 'Select a CSV or Excel file to import.',
		importModeLabel: 'Import mode',
		fullReplacementLabel: 'Full replacement',
		fullReplacementDescription:
			'Replace all existing records. Records not in the file will be removed.',
		partialUpdateLabel: 'Partial update',
		partialUpdateDescription:
			'Only add or update records from the file. Existing records not in the file will be preserved.',
		passwordProtectedError:
			'This file is password-protected. Password-procted files are not supported.',
		parseError: 'Could not read the file. Please check that the file is valid and try again.'
	},

	[LanguageType.Norwegian]: {
		title: 'Velg fil',
		description: 'Velg en CSV eller Excel-fil for å importere.',
		importModeLabel: 'Importmodus',
		fullReplacementLabel: 'Full erstatning',
		fullReplacementDescription:
			'Erstatt alle eksisterende oppføringer. Oppføringer som ikke er i filen vil bli fjernet.',
		partialUpdateLabel: 'Delvis oppdatering',
		partialUpdateDescription:
			'Kun legg til eller oppdater oppføringer fra filen. Eksisterende oppføringer som ikke er i filen vil bli bevart.',
		passwordProtectedError:
			'Denne filen er passordbeskyttet. Passordbeskyttede filer støttes ikke.',
		parseError: 'Kunne ikke lese filen. Vennligst sjekk at filen er gyldig og prøv igjen.'
	}
} as const;

export const parseCsv = (text: string) => {
	const result = Papa.parse<Record<string, string>>(text, {
		header: true,
		skipEmptyLines: 'greedy'
	});

	if (result.data.length === 0) return { columns: [], rows: [] };

	const allColumns = result.meta.fields ?? [];
	const columns = allColumns.filter(col => col.trim() !== '');

	const rows = result.data.map(row => {
		const filtered: Record<string, string> = {};
		columns.forEach(col => {
			filtered[col] = row[col] ?? '';
		});
		return filtered;
	});

	return { columns, rows };
};
